import { X, CheckCircle, Loader2 } from 'lucide-react';
import { useGetCheckOptionsQuery } from '../../store/api/apiSlice';
import type { CheckOption } from '../../types';

interface SelectCheckOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPackage: (packageId: string) => void;
}

// Format price in Naira
function formatNaira(price: number): string {
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(price);
}

// Fallback data if API is unavailable
const FALLBACK_OPTIONS: CheckOption[] = [
  {
    id: 'standard',
    name: 'Standard Check',
    price: 56900,
    currency: 'NGN',
    popular: false,
    features: [
      '40 pages of AI-powered contract review',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Validity: 30 days',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Support: Basic (Email, FAQs, Resources)',
    ],
  },
  {
    id: 'enhanced',
    name: 'Enhanced Check',
    price: 78900,
    currency: 'NGN',
    popular: true,
    features: [
      '40 pages of AI-powered contract review',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Validity: 30 days',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Support: Priority (Email, Phone, Live Chat)',
    ],
  },
  {
    id: 'pro',
    name: 'Pro Check',
    price: 56900,
    currency: 'NGN',
    popular: false,
    features: [
      '40 pages of AI-powered contract review',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Validity: 30 days',
      '200 words of human expert legal review',
      '200 words of human expert legal review',
      'AI obligation reminders',
      'Support: Basic (Email, FAQs, Resources)',
    ],
  },
];

function PricingCard({
  option,
  onSelect,
}: {
  option: CheckOption;
  onSelect: () => void;
}) {
  const isDark = option.popular;

  return (
    <div className="w-full sm:w-[370px] shrink-0">
      {/* Most Popular Badge */}
      {isDark && (
        <div className="flex justify-center mb-0">
          <div className="bg-[#f37b23] text-white text-sm font-normal tracking-[-0.5px] px-8 py-3 rounded-full border-[8px] border-white relative z-10 translate-y-[28px]">
            MOST POPULAR
          </div>
        </div>
      )}
      <div
        className={`${isDark ? 'bg-[#14171a]' : 'bg-[#f5f5f5]'} rounded-3xl relative overflow-hidden pb-20`}
      >
        <div className={`px-6 md:px-10 ${isDark ? 'pt-[54px]' : 'pt-[34px]'} flex flex-col gap-[14px]`}>
          <h3
            className={`text-2xl md:text-[36px] font-bold tracking-[-0.72px] leading-tight md:leading-[52px] ${isDark ? 'text-white' : 'text-black'}`}
          >
            {option.name}
          </h3>
          <div className={`w-full h-px ${isDark ? 'bg-white/20' : 'bg-[#d9d9d9]'}`} />
          <p className="text-2xl md:text-[36px] font-bold text-[#f37b23] tracking-[-0.96px] leading-tight md:leading-[52px]">
            {formatNaira(option.price)}
          </p>
          <div className={`w-full h-px ${isDark ? 'bg-white/20' : 'bg-[#d9d9d9]'}`} />
          <div className="flex flex-col gap-[13px]">
            {option.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-[10px]">
                <CheckCircle
                  className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-500'}`}
                />
                <span
                  className={`text-sm leading-[22px] text-left ${isDark ? 'text-white' : 'text-[#1b1b1b]'}`}
                >
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <button
            onClick={onSelect}
            className={`${isDark ? 'bg-[#f37b23] hover:bg-[#e06a15]' : 'bg-[#151515] hover:bg-[#2a2a2a]'} text-white text-sm font-medium px-4 py-2 rounded-lg w-[144px] h-10 transition-colors`}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SelectCheckOptionsModal({
  isOpen,
  onClose,
  onSelectPackage,
}: SelectCheckOptionsModalProps) {
  const { data: checkOptions, isLoading, error } = useGetCheckOptionsQuery(undefined, { skip: !isOpen });

  if (!isOpen) return null;

  const options = checkOptions && checkOptions.length > 0 ? checkOptions : FALLBACK_OPTIONS;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-[1260px] bg-white rounded-2xl md:rounded-3xl shadow-[0px_4px_18px_0px_rgba(0,0,0,0.09)] px-4 md:px-[54px] py-[26px] max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-[22px]">
          <h2 className="text-xl md:text-[32px] font-bold text-[#151515] tracking-[-1px] leading-tight md:leading-[56px]">
            Select Check Options
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#e8e8e8] hover:bg-gray-300 transition-colors shrink-0"
          >
            <X className="w-5 h-5 text-[#151515]" />
          </button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-10 h-10 text-[#f37b23] animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="text-center py-8 text-sm text-red-500 mb-4">
            Failed to load pricing. Showing default options.
          </div>
        )}

        {/* Pricing Cards */}
        {!isLoading && (
          <div className="flex gap-4 md:gap-[47px] items-end justify-center flex-wrap lg:flex-nowrap">
            {options.map((option) => (
              <PricingCard
                key={option.id}
                option={option}
                onSelect={() => onSelectPackage(option.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
